import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../services/AuthContext';
import ApiService from '../services/ApiService';
import ChartCard from '../components/ChartCard';
import LoadingIndicator from '../components/LoadingIndicator';
import {
  FILTERS,
  FILTERS_BY_ID,
  PRESETS,
  DEFAULT_SORT,
  SORT_OPTIONS,
  TABS,
  applyPreset,
  buildCsv,
  buildDistinctOptions,
  industriesForSector,
  updateFilterState,
  countActiveByTab,
  createDefaultState,
  describeCriteria,
  filterStocks,
  isPresetActive,
  nextSort,
  normalizeStock,
  parseTickers,
  sortKey,
  sortStocks,
  summarizeMatches,
  summarizeTrends,
  TREND_TILES,
  validateSavedState,
  watchlistPreset,
} from '../screener/screenerFilters';
import {
  deleteScreen,
  loadSavedScreens,
  loadWatchlist,
  normalizeScreenName,
  saveScreen,
  saveWatchlist,
} from '../screener/savedScreens';
import { buildScreenerNavState, parseScreenerNavState } from '../screener/chartNav';

const TABLE_PAGE = 100;
const GALLERY_PAGE = 24;

const fmt = (v, digits = 2) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(digits) : 'N/A');
const fmtPct = (v, digits = 1) => (typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(digits)}%` : 'N/A');
// Very negative PEGs (tiny negative growth) can run to thousands; floor the
// display at -99.00 so they don't widen the column. Sorting uses the raw value.
const PEG_DISPLAY_FLOOR = -99;
const fmtPeg = (v) => fmt(typeof v === 'number' && v < PEG_DISPLAY_FLOOR ? PEG_DISPLAY_FLOOR : v);

/** Uppercase a controlled text input's value without moving the caret. */
function toUpperPreservingCaret(event) {
  const input = event.target;
  const { selectionStart, selectionEnd } = input;
  const upper = input.value.toUpperCase();
  if (upper !== input.value) {
    input.value = upper;
    if (selectionStart !== null) input.setSelectionRange(selectionStart, selectionEnd);
  }
  return upper;
}

const VIEW_STORAGE_KEY = 'screener_results_view';
const VIEWS = [
  { id: 'table', label: 'Table', icon: '☰' },
  { id: 'gallery', label: 'Charts', icon: '▦' },
];

function readStoredView() {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return VIEWS.some((v) => v.id === stored) ? stored : 'table';
  } catch (error) {
    return 'table';
  }
}

// Every column is sortable by its `field` on the normalized stock record.
// Text columns start A→Z (dir 1); numeric columns start high→low (dir -1).
const TABLE_COLUMNS = [
  {
    label: 'Ticker', field: 'ticker', dir: 1, align: 'left', cellClass: 'font-semibold',
    // `nav` is the Screener nav state (chartNav) so the detail view's
    // Next/Previous walk the same filtered, sorted set as this table.
    render: (s, nav) => <Link to={`/chart/${s.id}`} state={{ screener: nav }} className="text-blue-700 hover:underline">{s.ticker}</Link>,
  },
  {
    label: 'Company', field: 'companyName', dir: 1, align: 'left', cellClass: 'text-xs max-w-[8.5rem] truncate',
    render: (s) => <span title={s.companyName || ''}>{s.companyName || 'N/A'}</span>,
  },
  {
    label: 'Sector', field: 'sector', dir: 1, align: 'left', cellClass: 'text-xs max-w-[6rem] truncate',
    render: (s) => <span title={s.sector || ''}>{s.sector || 'N/A'}</span>,
  },
  { label: 'Trend', field: 'trend_state', render: (s) => (s.trend_state === 1 ? 'Up' : s.trend_state === 0 ? 'Down' : 'N/A') },
  { label: 'Z 21d', field: 'zscore_21', dir: 1, render: (s) => fmt(s.zscore_21) },
  { label: 'Z 60d', field: 'zscore_60', dir: 1, render: (s) => fmt(s.zscore_60) },
  { label: 'Z 200d', field: 'zscore_200', dir: 1, render: (s) => fmt(s.zscore_200) },
  { label: 'Upside 6m', field: 'pcnt_upside_in_range_6m', render: (s) => fmtPct(s.pcnt_upside_in_range_6m) },
  { label: 'PEG', field: 'peg_ratio', dir: 1, render: (s) => <span title={fmt(s.peg_ratio)}>{fmtPeg(s.peg_ratio)}</span> },
  { label: 'P/S', field: 'ps_ratio', dir: 1, render: (s) => fmt(s.ps_ratio) },
  { label: 'Rating', field: 'analyst_rating_rank', cellClass: 'text-xs', title: 'FMP rating (sorts Strong Buy → Strong Sell)', render: (s) => s.analyst_rating_label || 'N/A' },
  // Soft hyphen lets the long single-word header wrap as "Pio-/troski".
  { label: 'Pio\u00ADtroski', field: 'piotroski_f_score', title: 'Piotroski F-score', render: (s) => fmt(s.piotroski_f_score, 0) },
  { label: 'Debt', field: 'debt_ratio', dir: 1, title: 'Debt ratio: total debt ÷ total assets', render: (s) => fmt(s.debt_ratio) },
  { label: 'ROE', field: 'roe', render: (s) => fmtPct(s.roe) },
  { label: 'Rule of 40', field: 'rule_of_40', render: (s) => fmt(s.rule_of_40, 1) },
  { label: 'Rev 1y', field: 'rev_gr_1yr', render: (s) => fmtPct(s.rev_gr_1yr) },
  { label: 'Rev Fcst', field: 'forecast_rev_gr', render: (s) => fmtPct(s.forecast_rev_gr) },
];

/**
 * The pipeline writes metadata.last_updated when the import finishes. Values
 * are timezone-aware UTC since the 2026-10-28 fix; older rows are the
 * importing server's naive local time (Railway runs UTC), so treat any
 * offset-less timestamp as UTC to render one consistent clock time.
 */
function parseImportTimestamp(iso) {
  if (!iso) return null;
  const raw = String(iso).trim();
  const hasOffset = /z$/i.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw);
  const date = new Date(hasOffset ? raw : `${raw}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

const fmtImportTimestamp = (date) => date.toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric',
  hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
});

function sortDescription(sort) {
  const option = SORT_OPTIONS.find((o) => o.value === sortKey(sort));
  if (option) return option.label;
  const column = TABLE_COLUMNS.find((c) => c.field === sort.field);
  const text = column && column.align === 'left';
  const arrow = sort.dir === 1 ? (text ? 'A → Z' : 'low → high') : (text ? 'Z → A' : 'high → low');
  return `${column ? column.label : sort.field} (${arrow})`;
}

function FilterSelect({ filter, value, onChange, options }) {
  const id = `screener-${filter.id}`;
  const active = value !== 'all';
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-xs font-medium text-gray-600 mb-1" title={filter.help}>
        {filter.label}
        {filter.help && <span className="ml-1 text-gray-400 cursor-help" aria-hidden="true">ⓘ</span>}
      </label>
      <select
        id={id}
        value={value}
        title={filter.help}
        onChange={(e) => onChange(filter.id, e.target.value)}
        className={`w-full min-w-0 border rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          active ? 'border-blue-500 font-medium text-navy-900' : 'border-gray-300 text-gray-700'
        }`}
      >
        <option value="all">{filter.type === 'dynamic' ? 'All' : 'Any'}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

const ScreenerPage = () => {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid || null;
  const location = useLocation();

  const [universe, setUniverse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dataFreshness, setDataFreshness] = useState(null);

  // Arriving via "Back to Search Results" from a chart detail page: seed the
  // filters/sort the user had so the screener shows the same result set.
  // (ScreenerPage remounts on every route change, so initializers suffice.)
  const navSeed = useMemo(() => parseScreenerNavState(location.state?.screener), [location.state]);
  const [filterState, setFilterState] = useState(() => navSeed?.filterState ?? createDefaultState());
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [sort, setSort] = useState(() => navSeed?.sort ?? DEFAULT_SORT);
  const [view, setView] = useState(readStoredView);
  const [visibleCount, setVisibleCount] = useState(TABLE_PAGE);

  const [savedScreens, setSavedScreens] = useState([]);
  const [selectedScreen, setSelectedScreen] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');

  const [watchlist, setWatchlist] = useState([]);
  const [watchEditOpen, setWatchEditOpen] = useState(false);
  const [watchDraft, setWatchDraft] = useState('');

  const tabRefs = useRef({});

  // Load universe
  const loadUniverse = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await ApiService.getScreenerUniverse();
      setUniverse((result.data || []).map(normalizeStock).filter((s) => s.ticker));
    } catch (error) {
      setLoadError(error.message || 'Failed to load the stock universe');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUniverse(); }, [loadUniverse]);

  // When the nightly pipeline last finished importing this dataset.
  useEffect(() => {
    let cancelled = false;
    ApiService.getMetadata()
      .then((metadata) => {
        const stamp = parseImportTimestamp(metadata.last_updated);
        if (!cancelled && stamp) setDataFreshness(fmtImportTimestamp(stamp));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Load per-user persisted screens + watchlist
  useEffect(() => {
    const { filters, error } = loadSavedScreens(userId);
    setSavedScreens(filters);
    if (error) toast.error(error);
    setWatchlist(loadWatchlist(userId));
  }, [userId]);

  const dynamicOptions = useMemo(() => ({
    sector: buildDistinctOptions(universe, 'sector').map((v) => ({ value: v, label: v })),
    industry: industriesForSector(universe, filterState.sectorFilter).map((v) => ({ value: v, label: v })),
  }), [universe, filterState.sectorFilter]);

  // One predicate evaluation per data/state change; every consumer uses it.
  const { matched, filterError } = useMemo(() => {
    try {
      return { matched: sortStocks(filterStocks(universe, filterState), sort), filterError: null };
    } catch (error) {
      return { matched: [], filterError: error.message };
    }
  }, [universe, filterState, sort]);

  useEffect(() => {
    setVisibleCount(view === 'table' ? TABLE_PAGE : GALLERY_PAGE);
  }, [filterState, sort, view]);

  const changeView = (nextView) => {
    setView(nextView);
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, nextView); } catch (error) { /* view still switches */ }
  };

  const handleColumnSort = (column) => setSort((current) => nextSort(current, column.field, column.dir ?? -1));

  const galleryStocks = useMemo(() => matched.filter((s) => s.has_chart), [matched]);
  const missingCharts = matched.length - galleryStocks.length;
  // Nav context handed to /chart/:id from each view: the gallery shows only
  // chart-eligible stocks, the table shows every match.
  const galleryNav = useMemo(() => buildScreenerNavState({ filterState, sort, chartOnly: true }), [filterState, sort]);
  const tableNav = useMemo(() => buildScreenerNavState({ filterState, sort, chartOnly: false }), [filterState, sort]);
  const tabCounts = useMemo(() => countActiveByTab(filterState), [filterState]);
  const criteria = useMemo(() => describeCriteria(filterState), [filterState]);
  const stats = useMemo(() => summarizeMatches(matched), [matched]);
  // Trend tiles ignore the Trend filter itself so they stay a breakdown of the
  // rest of the screen (otherwise clicking Uptrend would just show 100%).
  const trendStats = useMemo(() => {
    try {
      return summarizeTrends(filterStocks(universe, { ...filterState, trendStateFilter: 'all' }));
    } catch (error) {
      return null;
    }
  }, [universe, filterState]);
  const myWatch = useMemo(() => watchlistPreset(watchlist.join(', ')), [watchlist]);

  const setFilter = useCallback((id, value) => {
    setFilterState((prev) => updateFilterState(prev, id, value, universe));
  }, [universe]);

  const handlePreset = (preset) => setFilterState((prev) => applyPreset(prev, preset));

  const handleReset = () => {
    setFilterState(createDefaultState());
    setSelectedScreen('');
  };

  const handleTabKey = (event, index) => {
    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = TABS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextId = TABS[nextIndex].id;
    setActiveTab(nextId);
    tabRefs.current[nextId]?.focus();
  };

  // Saved screens
  const handleSaveScreen = (event) => {
    event.preventDefault();
    const name = normalizeScreenName(saveName);
    if (!name) {
      toast.error('Enter a name for this screen.');
      return;
    }
    const exists = savedScreens.some((f) => f.name === name);
    if (exists && !window.confirm(`A screen named "${name}" already exists. Overwrite it?`)) return;
    try {
      const next = saveScreen(userId, savedScreens, name, filterState);
      setSavedScreens(next);
      setSelectedScreen(name);
      setSaveOpen(false);
      setSaveName('');
      toast.success(exists ? `Screen "${name}" overwritten` : `Screen "${name}" saved`);
    } catch (error) {
      toast.error(`Screen not saved: ${error.message}`);
    }
  };

  const handleLoadScreen = () => {
    const screen = savedScreens.find((f) => f.name === selectedScreen);
    if (!screen) {
      toast.error('Choose a saved screen to load.');
      return;
    }
    const { state, errors } = validateSavedState(screen.state);
    if (errors.length) {
      toast.error(`"${screen.name}" can't be loaded: ${errors.join(' ')} Delete and re-save it.`);
      return;
    }
    setFilterState(state);
    toast.info(`Loaded "${screen.name}"`);
  };

  const handleDeleteScreen = () => {
    if (!selectedScreen) return;
    if (!window.confirm(`Delete saved screen "${selectedScreen}"?`)) return;
    try {
      const next = deleteScreen(userId, savedScreens, selectedScreen);
      setSavedScreens(next);
      toast.success(`Deleted "${selectedScreen}"`);
      setSelectedScreen('');
    } catch (error) {
      setSavedScreens(loadSavedScreens(userId).filters);
      toast.error(`Screen not deleted: ${error.message}`);
    }
  };

  // Watchlist
  const openWatchEdit = () => {
    setWatchDraft(watchlist.join(', '));
    setWatchEditOpen(true);
  };

  const handleWatchSave = () => {
    try {
      const tickers = saveWatchlist(userId, watchDraft);
      setWatchlist(tickers);
      setWatchEditOpen(false);
      toast.success(`MyWatch saved (${tickers.length} tickers)`);
    } catch (error) {
      toast.error(`MyWatch not saved: ${error.message}`);
    }
  };

  const handleExport = () => {
    if (matched.length === 0) {
      toast.info('No matching stocks to export.');
      return;
    }
    const blob = new Blob([buildCsv(matched)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ste_screener_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const presetButtonClass = (active) => `px-3 py-1.5 rounded-md text-sm font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    active ? 'bg-navy-900 text-white border-navy-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
  }`;

  const tabFilters = FILTERS.filter((f) => f.category === activeTab);

  return (
    <div className="space-y-4" data-testid="screener-page">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Stock Screener <span className="text-sm font-medium text-blue-600 align-middle">beta</span></h1>
          <p className="text-sm text-gray-600">Every filter applies instantly and combines with AND.</p>
        </div>
        {dataFreshness && (
          <div
            className="text-xs text-gray-500 whitespace-nowrap"
            title="When the nightly data pipeline last finished importing this dataset. Chart images always show prices through the prior trading day's close (the 'As of' date printed on each chart)."
            data-testid="screener-data-freshness"
          >
            Data updated: <span className="font-medium text-gray-700">{dataFreshness}</span>
          </div>
        )}
      </div>

      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 flex items-center justify-between" role="alert">
          <span>{loadError}</span>
          <button type="button" onClick={loadUniverse} className="underline font-medium">Retry</button>
        </div>
      )}

      {/* Quick filters */}
      <section className="bg-white border border-gray-200 rounded-xl p-4" aria-label="Quick filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="min-w-0">
            <label htmlFor="screener-tickerFilter" className="block text-xs font-medium text-gray-600 mb-1" title={FILTERS_BY_ID.tickerFilter.help}>
              Tickers <span className="text-gray-400 cursor-help" aria-hidden="true">ⓘ</span>
            </label>
            <input
              id="screener-tickerFilter"
              type="text"
              value={filterState.tickerFilter}
              onChange={(e) => setFilter('tickerFilter', e.target.value)}
              placeholder="e.g. AAPL, MSFT NVDA"
              title={FILTERS_BY_ID.tickerFilter.help}
              className={`w-full border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                parseTickers(filterState.tickerFilter).length ? 'border-blue-500' : 'border-gray-300'
              }`}
            />
          </div>
          <FilterSelect filter={FILTERS_BY_ID.sectorFilter} value={filterState.sectorFilter} onChange={setFilter} options={dynamicOptions.sector} />
          <FilterSelect filter={FILTERS_BY_ID.industryFilter} value={filterState.industryFilter} onChange={setFilter} options={dynamicOptions.industry} />
        </div>
      </section>

      {/* Category tabs */}
      <section className="bg-white border border-gray-200 rounded-xl" aria-label="Filter categories">
        <div role="tablist" aria-label="Filter categories" className="flex flex-wrap border-b border-gray-200 px-2">
          {TABS.map((tab, index) => {
            const selected = tab.id === activeTab;
            const count = tabCounts[tab.id];
            return (
              <button
                key={tab.id}
                ref={(el) => { tabRefs.current[tab.id] = el; }}
                type="button"
                role="tab"
                id={`screener-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`screener-panel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(e) => handleTabKey(e, index)}
                className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  selected ? 'border-blue-600 text-navy-900' : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {tab.label}
                {count > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-blue-600 text-white text-xs" aria-label={`${count} active`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div
          role="tabpanel"
          id={`screener-panel-${activeTab}`}
          aria-labelledby={`screener-tab-${activeTab}`}
          className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3"
        >
          {tabFilters.map((f) => (
            <FilterSelect key={f.id} filter={f} value={filterState[f.id]} onChange={setFilter} options={f.options} />
          ))}
        </div>
      </section>

      {/* Presets, watchlist, saved screens */}
      <section className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" aria-label="Presets and saved screens">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 mr-1">Presets</span>
          {PRESETS.map((preset) => {
            const active = isPresetActive(filterState, preset);
            return (
              <button key={preset.id} type="button" aria-pressed={active} title={preset.help} onClick={() => handlePreset(preset)} className={presetButtonClass(active)}>
                {preset.label}
              </button>
            );
          })}
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              aria-pressed={isPresetActive(filterState, myWatch)}
              disabled={watchlist.length === 0}
              title={watchlist.length ? `MyWatch: ${watchlist.join(', ')}` : 'Your watchlist is empty — click Edit to add tickers'}
              onClick={() => handlePreset(myWatch)}
              className={`${presetButtonClass(isPresetActive(filterState, myWatch))} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              MyWatch{watchlist.length ? ` (${watchlist.length})` : ''}
            </button>
            <button type="button" onClick={openWatchEdit} className="text-xs text-blue-600 hover:underline px-1">Edit</button>
          </span>
          <button type="button" onClick={handleReset} className="ml-auto px-3 py-1.5 rounded-md text-sm font-medium border border-red-300 text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500">
            Reset All
          </button>
        </div>

        {watchEditOpen && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <label htmlFor="screener-watchlist" className="block text-xs font-medium text-gray-600 mb-1">MyWatch tickers (comma or space separated)</label>
            <textarea id="screener-watchlist" rows={2} value={watchDraft} onChange={(e) => setWatchDraft(toUpperPreservingCaret(e))} autoCapitalize="characters" spellCheck={false} className="w-full uppercase border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={handleWatchSave} className="px-3 py-1.5 rounded-md text-sm font-medium bg-navy-900 text-white">Save</button>
              <button type="button" onClick={() => setWatchEditOpen(false)} className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 bg-white">Cancel</button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 mr-1">Saved screens</span>
          <label htmlFor="screener-saved" className="sr-only">Saved screens</label>
          <select id="screener-saved" value={selectedScreen} onChange={(e) => setSelectedScreen(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white min-w-[12rem] focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">{savedScreens.length ? 'Choose a screen…' : 'No saved screens yet'}</option>
            {savedScreens.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
          <button type="button" onClick={handleLoadScreen} disabled={!selectedScreen} className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50">Load &amp; Apply</button>
          <button type="button" onClick={handleDeleteScreen} disabled={!selectedScreen} className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50">Delete</button>
          <button type="button" onClick={() => setSaveOpen((o) => !o)} className="px-3 py-1.5 rounded-md text-sm font-medium bg-navy-900 text-white hover:bg-navy-800">Save Filter</button>
        </div>

        {saveOpen && (
          <form onSubmit={handleSaveScreen} className="flex flex-wrap items-center gap-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <label htmlFor="screener-save-name" className="text-sm text-gray-700">Name</label>
            <input id="screener-save-name" type="text" value={saveName} maxLength={80} onChange={(e) => setSaveName(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm flex-1 min-w-[12rem] focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. Quality on a dip" />
            <button type="submit" className="px-3 py-1.5 rounded-md text-sm font-medium bg-navy-900 text-white">Save</button>
            <button type="button" onClick={() => setSaveOpen(false)} className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 bg-white">Cancel</button>
            <p className="w-full text-xs text-gray-500">Saves every control (not just the open tab) in this browser.</p>
          </form>
        )}
      </section>

      {/* Active criteria */}
      <section className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-sm" aria-label="Active criteria" data-testid="screener-criteria">
        <span className="font-semibold text-navy-900 mr-2">Active criteria:</span>
        {criteria.length === 0 ? (
          <span className="text-gray-600">None (all tickers shown)</span>
        ) : (
          <span className="inline-flex flex-wrap gap-2 align-middle">
            {criteria.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1 bg-white border border-blue-200 rounded-full pl-2 pr-1 py-0.5">
                <span className="text-gray-600">{c.label}:</span>
                <span className="font-medium text-navy-900">{c.valueLabel}</span>
                <button type="button" onClick={() => setFilter(c.id, c.id === 'tickerFilter' ? '' : 'all')} className="ml-1 text-gray-400 hover:text-gray-700 px-1" aria-label={`Clear ${c.label}`}>✕</button>
              </span>
            ))}
          </span>
        )}
      </section>

      {/* Results toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-700" data-testid="screener-count" aria-live="polite">
          Showing <strong>{matched.length.toLocaleString()}</strong> of <strong>{universe.length.toLocaleString()}</strong>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="screener-sort" className="text-sm text-gray-600">Sort</label>
          <select
            id="screener-sort"
            value={sortKey(sort)}
            onChange={(e) => {
              const [field, dir] = e.target.value.split(':');
              setSort({ field, dir: Number(dir) === 1 ? 1 : -1 });
            }}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {!SORT_OPTIONS.some((o) => o.value === sortKey(sort)) && (
              <option value={sortKey(sort)}>{sortDescription(sort)}</option>
            )}
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="inline-flex bg-gray-100 rounded-lg p-1 border border-gray-200" role="radiogroup" aria-label="Result view">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                role="radio"
                aria-checked={view === v.id}
                onClick={() => changeView(v.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  view === v.id ? 'bg-navy-900 text-white shadow-sm' : 'text-gray-600 hover:text-navy-900'
                }`}
              >
                <span aria-hidden="true">{v.icon}</span>
                {v.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={handleExport} disabled={matched.length === 0} className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50">
            Export CSV
          </button>
        </div>
      </div>

      {universe.length > 0 && (trendStats || matched.length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-center text-xs" aria-label="Match statistics">
          {trendStats && TREND_TILES.map((tile) => {
            const active = filterState.trendStateFilter === tile.value;
            const figure = trendStats[tile.value];
            return (
              <button
                key={tile.value}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter('trendStateFilter', active ? 'all' : tile.value)}
                title={`${tile.help}. ${active ? 'Click to clear this Trend filter.' : 'Click to filter to these stocks.'}`}
                className={`rounded-lg py-2 border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  active ? 'bg-navy-900 border-navy-900 text-white' : 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <div className={active ? 'text-blue-100' : 'text-gray-500'}>{tile.label}</div>
                <div className={`text-base font-semibold ${active ? 'text-white' : 'text-navy-900'}`}>{fmtPct(figure.pct, 0)}</div>
                <div className={active ? 'text-blue-100' : 'text-gray-400'}>{figure.count.toLocaleString('en-US')} stocks</div>
              </button>
            );
          })}
          {[
            ['Mean PSS / Mean SS', fmt(stats.pssToSsRatio), `Mean PSS ${fmt(stats.meanPredicted)} ÷ Mean SS ${fmt(stats.meanSignal)}`],
            ['Median PSS / Median SS', fmt(stats.medianPssToSsRatio), `Median PSS ${fmt(stats.medianPredicted)} ÷ Median SS ${fmt(stats.medianSignal)}${stats.medianSignal === 0 ? ' (no ratio when median SS is 0)' : ''}`],
          ].map(([label, value, title]) => (
            <div key={label} title={title} className="bg-white border border-gray-200 rounded-lg py-2 flex flex-col justify-center">
              <div className="text-gray-500">{label}</div>
              <div className="text-base font-semibold text-navy-900">{value}</div>
            </div>
          ))}
        </div>
      )}

      {filterError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3" role="alert">{filterError}</div>
      )}

      {loading ? (
        <LoadingIndicator />
      ) : matched.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center" data-testid="screener-empty">
          <p className="text-lg font-medium text-navy-900">No stocks match these criteria.</p>
          <p className="text-sm text-gray-600 mt-1">Loosen or clear a filter above, or use Reset All.</p>
        </div>
      ) : view === 'table' ? (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="screener-table">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                {TABLE_COLUMNS.map((c) => {
                  const active = sort.field === c.field;
                  const ariaSort = active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none';
                  return (
                    <th key={c.field} scope="col" aria-sort={ariaSort} className={`px-0.5 py-1 align-bottom ${c.align === 'left' ? 'text-left' : 'text-right'}`}>
                      <button
                        type="button"
                        onClick={() => handleColumnSort(c)}
                        title={`${c.title ? `${c.title}. ` : ''}Click to sort${active ? ' (click again to reverse)' : ''}`}
                        className={`flex w-full items-end gap-0.5 px-1 py-1 rounded uppercase leading-tight hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                          c.align === 'left' ? 'justify-start text-left' : 'justify-end text-right'
                        } ${active ? 'text-navy-900 font-semibold' : ''}`}
                      >
                        <span>{c.label}</span>
                        <span aria-hidden="true" className={`shrink-0 ${active ? 'text-blue-600' : 'text-gray-300'}`}>
                          {active ? (sort.dir === 1 ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {matched.slice(0, visibleCount).map((s) => (
                <tr key={s.id ?? s.ticker} className="hover:bg-gray-50">
                  {TABLE_COLUMNS.map((c) => (
                    <td
                      key={c.field}
                      className={`px-1.5 py-1.5 whitespace-nowrap ${c.align === 'left' ? 'text-left' : 'text-right tabular-nums'} ${c.cellClass || ''} ${
                        sort.field === c.field ? 'bg-blue-50/40' : ''
                      }`}
                    >
                      {c.render(s, tableNav)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {visibleCount < matched.length && (
            <div className="p-3 text-center border-t border-gray-100">
              <button type="button" onClick={() => setVisibleCount((n) => n + TABLE_PAGE)} className="text-sm font-medium text-blue-600 hover:underline">
                Show more ({(matched.length - visibleCount).toLocaleString()} remaining)
              </button>
            </div>
          )}
        </div>
      ) : (
        <div data-testid="screener-gallery">
          <p className="text-xs text-gray-500 mb-2">Sorted by {sortDescription(sort)} — same order as the Table.</p>
          {missingCharts > 0 && (
            <p className="text-xs text-gray-500 mb-2">{missingCharts.toLocaleString()} matching stock{missingCharts === 1 ? ' has' : 's have'} no chart and {missingCharts === 1 ? 'is' : 'are'} listed only in the Table view.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryStocks.slice(0, visibleCount).map((s, index) => (
              <ChartCard key={s.id ?? s.ticker} stock={s.raw} currentFilters={{}} currentPage={1} stockIndex={index} screenerNav={galleryNav} />
            ))}
          </div>
          {visibleCount < galleryStocks.length && (
            <div className="p-3 text-center">
              <button type="button" onClick={() => setVisibleCount((n) => n + GALLERY_PAGE)} className="text-sm font-medium text-blue-600 hover:underline">
                Show more ({(galleryStocks.length - visibleCount).toLocaleString()} remaining)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScreenerPage;
